import { useCallback, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import copy from '~/assets/icons/copy.svg';
import { rage } from '~/rage';
import {
	StyledArrow,
	StyledCopyButton,
	StyledCopyContainer,
	StyledInput,
	StyledInputArrowContainer,
	StyledPopup,
	StyledSelectorContainer,
	StyledTextContainer,
	StyledTitleContainer,
} from './Selector.styled';

interface ISelectorProps {
	title: string;
	index: number;
	maxIndex: number;
	minIndex?: number;
	modIdsByIndex?: Record<number, string>;
	onChange?: (index: number) => void;
}

const Selector: React.FC<ISelectorProps> = ({
	title,
	index,
	maxIndex,
	minIndex = -1,
	modIdsByIndex,
	onChange,
}) => {
	const [currentIndex, setCurrentIndex] = useState(index);
	const [popupVisible, setPopupVisible] = useState(false);
	const currentModId = modIdsByIndex?.[currentIndex];

	const handleCurrentIndexChange = useCallback(
		(newIndex: number) => {
			newIndex = Math.min(Math.max(newIndex, minIndex), maxIndex);
			if (newIndex === currentIndex) return;

			onChange?.(newIndex);
			setCurrentIndex(newIndex);
		},
		[currentIndex, maxIndex, minIndex, onChange],
	);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const cleanedValue = e.target.value.replace(/[^\d-]/g, '');
		const newIndex =
			cleanedValue === '' || cleanedValue === '-'
				? 0
				: parseInt(cleanedValue, 10);

		if (!isNaN(newIndex)) {
			handleCurrentIndexChange(newIndex);
		}
	};

	const handleCopyModId = useCallback(() => {
		if (!currentModId) {
			return;
		}

		rage.tuningTester.copyModId(currentModId);
		rage.notifications.showInfo('Текст скопирован в буфер обмена');
	}, [currentModId]);

	return (
		<StyledSelectorContainer>
			<StyledTextContainer>
				<StyledTitleContainer>
					<span>{title}</span>
					<StyledCopyContainer
						$visible={!!currentModId}
						onMouseEnter={() => setPopupVisible(true)}
						onMouseLeave={() => setPopupVisible(false)}
					>
						<StyledCopyButton
							type="button"
							onClick={handleCopyModId}
							title="Скопировать ID тюнинга"
							aria-label="Скопировать ID тюнинга"
						>
							<img src={copy} alt="" />
						</StyledCopyButton>
						{currentModId && (
							<CSSTransition
								in={popupVisible}
								timeout={250}
								classNames="popup"
								unmountOnExit
							>
								<StyledPopup text={currentModId} />
							</CSSTransition>
						)}
					</StyledCopyContainer>
				</StyledTitleContainer>
				<span>
					{currentIndex}/{maxIndex}
				</span>
			</StyledTextContainer>
			<StyledInputArrowContainer>
				<StyledArrow
					$direction={'left'}
					onClick={() => handleCurrentIndexChange(currentIndex - 1)}
				/>
				<StyledInput
					value={currentIndex.toString()}
					onChange={handleInputChange}
				/>
				<StyledArrow
					$direction={'right'}
					onClick={() => handleCurrentIndexChange(currentIndex + 1)}
				/>
			</StyledInputArrowContainer>
		</StyledSelectorContainer>
	);
};

export { Selector };
